import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";

// Change / Swap room for a still-reserved line — a faithful port of the /admin room-booking-line
// modals (same endpoints + services): Change shows the available same-type rooms as a building/floor
// grid; Swap lists other same-date bookings and free-to-take blocked rooms. Pick one, then an in-app
// confirmation before the move is committed. `mode` is "change" or "swap".
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Intro = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Muted = styled.div`
  padding: 0.6rem 0.25rem;
  font-size: 0.9rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f2c4c0;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
`;

const BuildingBlock = styled.div`
  margin-bottom: 1rem;
`;

const BuildingName = styled.div`
  margin-bottom: 0.3rem;
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const FloorRow = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${theme.border};
  border-bottom: 0;
  &:last-child {
    border-bottom: 1px solid ${theme.border};
  }
`;

const FloorLabel = styled.div`
  flex: 0 0 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem;
  background: #f7f7f7;
  border-right: 1px solid ${theme.border};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${theme.ink700};
`;

const FloorRooms = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
`;

// Cell states mirror the /admin change-room grid: available (white, clickable, green hover), current
// (blue ring), selected (green fill), unavailable (grey, not clickable).
const Cell = styled.button`
  flex: 0 0 auto;
  min-width: 78px;
  padding: 0.35rem 0.4rem;
  border: 0;
  border-right: 1px solid ${theme.border};
  background: ${(props) =>
    props.$selected
      ? "#c8e6c9"
      : props.$current
        ? "#e3f2fd"
        : props.$available
          ? "#ffffff"
          : "#e9ecef"};
  box-shadow: ${(props) =>
    props.$selected
      ? "inset 0 0 0 2px #4caf50"
      : props.$current
        ? "inset 0 0 0 2px #2196f3"
        : "none"};
  color: ${(props) =>
    props.$available || props.$current ? theme.ink : "#adb5bd"};
  cursor: ${(props) =>
    props.$available && !props.$current ? "pointer" : "default"};
  text-align: center;
  &:hover {
    background: ${(props) =>
      props.$available && !props.$current && !props.$selected
        ? "#e8f5e9"
        : undefined};
  }
`;

const CellCode = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
`;

const CellNote = styled.div`
  font-size: 0.66rem;
  color: ${(props) => props.$color || theme.muted};
`;

const SwapGroupTitle = styled.div`
  margin: 0.6rem 0 0.4rem;
  font-size: 0.82rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const SwapItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.4rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid
    ${(props) => (props.$active ? theme.goldDark : theme.border)};
  border-radius: 9px;
  background: ${(props) => (props.$active ? theme.goldSoft : "#ffffff")};
  font-size: 0.92rem;
  color: ${theme.ink};
  text-align: left;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

const SwapBadge = styled.span`
  margin-left: auto;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

const ConfirmBox = styled.div`
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${theme.ink};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Selected = styled.span`
  margin-right: auto;
  font-size: 0.84rem;
  color: ${theme.muted};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const RoomMoveModal = (props) => {
  const { t } = useT();
  const mode = props.mode; // "change" | "swap"
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);

  const base = `/frontdesk_v2/bookings/${props.bookingId}`;

  useEffect(() => {
    let active = true;
    const url =
      mode === "swap"
        ? `${base}/swap_targets?change_line_id=${props.changeLineId}`
        : `${base}/available_rooms?change_line_id=${props.changeLineId}`;
    const run = async () => {
      setLoading(true);
      setError("");
      setPayload(null);
      setSelected(null);
      setConfirming(false);
      try {
        const res = await apiFetch(url);
        if (!active) {
          return;
        }
        if (res && res.data) {
          setPayload(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      } catch {
        if (active) {
          setError(t("error_body"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [base, props.changeLineId, mode, t]);

  const submit = async () => {
    setProcessing(true);
    setError("");
    const url = mode === "swap" ? `${base}/swap_room` : `${base}/change_room`;
    const body =
      mode === "swap"
        ? {
            change_line_id: props.changeLineId,
            target_line_id: selected?.lineId,
            target_room_id: selected?.roomId,
            line_id: props.anchorLineId,
          }
        : {
            change_line_id: props.changeLineId,
            new_room_id: selected?.roomId,
            line_id: props.anchorLineId,
          };
    try {
      const res = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (res?.data?.booking) {
        props.onChanged(res.data, res.message);
        props.onClose();
      } else {
        setError(res?.message || t("error_body"));
        setConfirming(false);
      }
    } catch {
      setError(t("error_body"));
      setConfirming(false);
    } finally {
      setProcessing(false);
    }
  };

  const title = mode === "swap" ? t("swap_room_title") : t("change_room_title");

  const confirmMessage =
    mode === "swap"
      ? `${t("confirm_swap_room")} ${props.currentCode || ""} ⇄ ${selected?.label || ""}`
      : `${t("confirm_change_room")} ${selected?.code || ""}`;

  const renderChangeGrid = () => {
    const buildings = payload?.buildings || [];
    if (buildings.length === 0) {
      return <Muted>{t("change_room_none")}</Muted>;
    }
    return buildings.map((building) => (
      <BuildingBlock key={building.id || building.name}>
        <BuildingName>{building.name || "—"}</BuildingName>
        {building.floors.map((floor) => (
          <FloorRow key={floor.id || floor.code}>
            <FloorLabel>{floor.code || floor.name || ""}</FloorLabel>
            <FloorRooms>
              {floor.rooms.map((room) => {
                const selectable = room.available && !room.current;
                return (
                  <Cell
                    key={room.id}
                    type="button"
                    $available={room.available}
                    $current={room.current}
                    $selected={selected?.roomId === room.id}
                    disabled={!selectable}
                    onClick={() =>
                      setSelected({ roomId: room.id, code: room.code })
                    }
                  >
                    <CellCode>{room.code}</CellCode>
                    {room.current ? (
                      <CellNote $color="#1d5fa8">
                        {t("change_room_current")}
                      </CellNote>
                    ) : !room.available ? (
                      <CellNote>{t("change_room_unavailable")}</CellNote>
                    ) : room.physical_status &&
                      room.physical_status !== "vacant_clean" ? (
                      <CellNote $color="#c2410c">
                        {t(`status_${room.physical_status}`)}
                      </CellNote>
                    ) : null}
                  </Cell>
                );
              })}
            </FloorRooms>
          </FloorRow>
        ))}
      </BuildingBlock>
    ));
  };

  const renderSwapLists = () => {
    const lines = payload?.line_targets || [];
    const rooms = payload?.room_targets || [];
    if (lines.length === 0 && rooms.length === 0) {
      return <Muted>{t("swap_room_none")}</Muted>;
    }
    return (
      <>
        {lines.length > 0 ? (
          <>
            <SwapGroupTitle>{t("swap_room_lines")}</SwapGroupTitle>
            {lines.map((target) => {
              const label = `${target.room_code} · ${target.guest || t("guest")}`;
              return (
                <SwapItem
                  key={target.line_id}
                  type="button"
                  $active={selected?.lineId === target.line_id}
                  onClick={() => setSelected({ lineId: target.line_id, label })}
                >
                  <strong>{target.room_code}</strong> —{" "}
                  {target.guest || t("guest")}
                  <SwapBadge $bg={theme.canvas} $fg={theme.ink700}>
                    {t("swap_room_same_dates")}
                  </SwapBadge>
                </SwapItem>
              );
            })}
          </>
        ) : null}
        {rooms.length > 0 ? (
          <>
            <SwapGroupTitle>{t("swap_room_blocked")}</SwapGroupTitle>
            {rooms.map((target) => (
              <SwapItem
                key={target.room_id}
                type="button"
                $active={selected?.roomId === target.room_id}
                onClick={() =>
                  setSelected({
                    roomId: target.room_id,
                    label: target.room_code,
                  })
                }
              >
                <strong>{target.room_code}</strong>
                <SwapBadge $bg="#fef3c7" $fg="#92400e">
                  {t("swap_room_free")}
                </SwapBadge>
              </SwapItem>
            ))}
          </>
        ) : null}
      </>
    );
  };

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            {title}
            {props.currentCode ? ` — ${props.currentCode}` : ""}
          </Title>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Body>
          {mode === "swap" ? <Intro>{t("swap_room_intro")}</Intro> : null}
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {loading ? (
            <Muted>{t("loading")}</Muted>
          ) : confirming ? (
            <ConfirmBox>{confirmMessage}</ConfirmBox>
          ) : mode === "swap" ? (
            renderSwapLists()
          ) : (
            renderChangeGrid()
          )}
        </Body>
        <Foot>
          <Selected>
            {selected
              ? `${t("change_room_selected")}: ${selected.code || selected.label}`
              : ""}
          </Selected>
          <Cancel
            type="button"
            onClick={() =>
              confirming ? setConfirming(false) : props.onClose()
            }
          >
            {t("confirm_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={!selected || processing}
            onClick={() => (confirming ? submit() : setConfirming(true))}
          >
            <Check size={15} />
            {confirming
              ? mode === "swap"
                ? t("swap_room_ok")
                : t("change_room_ok")
              : t("change_room_confirm")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default RoomMoveModal;
